/**
 * Privacy policy page (UK GDPR Art. 13/14 — transparency). Public route — no
 * auth required (users must be able to read the policy BEFORE signing up).
 * Draft British-English copy with [BUSINESS TO CONFIRM] placeholders.
 * British English project.
 */
import { useNavigate } from 'react-router-dom';
import './PrivacyPolicy.css';

export function PrivacyPolicy() {
  const navigate = useNavigate();
  const goBack = () => {
    // Return to the page the user came from (Settings, SignIn, Home, etc.).
    // Fall back to the home route if there's no browser history (deep link).
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="itk-legal-page">
      <div className="itk-legal-page__inner">
        <button type="button" className="itk-legal-page__back" onClick={goBack}>
          ← Back
        </button>

        <h1>Privacy policy</h1>
        <p className="itk-legal-page__updated">
          Last updated: 6 August 2026 <span className="itk-legal-flag">[DRAFT — BUSINESS TO CONFIRM]</span>
        </p>

        <section>
          <h2>1. Who we are</h2>
          <p>
            Innovate Design Ltd (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is the data controller
            responsible for your personal data in connection with the Idea Toolkit. We are registered in
            England and Wales.
          </p>
          <p className="itk-legal-flag">
            [BUSINESS TO CONFIRM: full registered company name, company number, registered office address,
            and any data protection officer or contact details.]
          </p>
          <p>
            <strong>Data protection contact:</strong>{' '}
            <span className="itk-legal-flag">[BUSINESS TO CONFIRM — DSAR contact email/address]</span>
          </p>
        </section>

        <section>
          <h2>2. What personal data we collect</h2>
          <p>We collect the following categories of personal data when you use the Idea Toolkit:</p>
          <ul>
            <li><strong>Account data</strong> — your email address (used as your sign-in identifier).</li>
            <li><strong>Idea data</strong> — the information you enter about your invention (patentability answers, business viability figures, desirability canvas, feasibility targets, idea name and details).</li>
            <li><strong>Confidentiality agreement data</strong> — your name and address as entered on the NDA form, and the signed agreement.</li>
            <li><strong>Booking data</strong> — your phone number, the idea name, the selected category, and your chosen review-call slot.</li>
            <li><strong>Uploaded documents</strong> — files you upload (drawings, descriptions) relating to your idea.</li>
            <li><strong>Learning progress</strong> — which commercialisation modules you have completed.</li>
            <li><strong>Device identifiers</strong> — web push tokens (if you enable push notifications).</li>
            <li><strong>Click attribution data</strong> — the Google Click ID (gclid) and UTM parameters from the link you used to reach us, and the landing page URL.</li>
            <li><strong>Usage data</strong> — pseudonymised analytics events (tied to your user id, not your email) about how you use the toolkit.</li>
          </ul>
        </section>

        <section>
          <h2>3. How we use your data and our lawful bases</h2>
          <table className="itk-legal-table">
            <thead>
              <tr>
                <th>Processing activity</th>
                <th>Purpose</th>
                <th>Lawful basis</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Creating and managing your account</td>
                <td>To provide the Idea Toolkit service</td>
                <td>Contract (taking steps before a contract)</td>
              </tr>
              <tr>
                <td>Storing your idea, learning progress and uploaded documents</td>
                <td>To help you develop and assess your invention</td>
                <td>Contract</td>
              </tr>
              <tr>
                <td>Generating and storing your signed confidentiality agreement</td>
                <td>To protect your idea during the review process</td>
                <td>Contract / legal obligation</td>
              </tr>
              <tr>
                <td>Booking and managing your review call</td>
                <td>To arrange the call with a matched Innovation Coach</td>
                <td>Contract</td>
              </tr>
              <tr>
                <td>Sending service notifications (booking reminders, idea completion nudges)</td>
                <td>To keep you informed about your booking and progress</td>
                <td>Contract / legitimate interest</td>
              </tr>
              <tr>
                <td>Sending marketing communications</td>
                <td>To tell you about products and services that may interest you</td>
                <td>Consent (you can opt out at any time)</td>
              </tr>
              <tr>
                <td>Matching you with an Innovation Coach and sharing data with our automation</td>
                <td>To assign the right coach and manage the review</td>
                <td>Contract / legitimate interest</td>
              </tr>
              <tr>
                <td>Recording pseudonymised analytics events</td>
                <td>To understand how the toolkit is used and improve it</td>
                <td>Legitimate interest</td>
              </tr>
              <tr>
                <td>Capturing click attribution (gclid, UTMs)</td>
                <td>To measure the effectiveness of our advertising</td>
                <td>Legitimate interest</td>
              </tr>
              <tr>
                <td>Retaining signed NDAs after account deletion</td>
                <td>To uphold the confidentiality agreement</td>
                <td>Legal obligation / legal claims (Art. 17(3)(e))</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>4. Who we share your data with (processors and recipients)</h2>
          <p>We use the following third-party processors to operate the Idea Toolkit:</p>
          <table className="itk-legal-table">
            <thead>
              <tr>
                <th>Processor</th>
                <th>Function</th>
                <th>Region</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Google Cloud / Firebase</td>
                <td>Hosting, authentication, database, file storage</td>
                <td>europe-west2 (London)</td>
              </tr>
              <tr>
                <td>Resend</td>
                <td>Transactional email delivery (OTP codes, booking confirmations)</td>
                <td>eu-west-1 (Ireland)</td>
              </tr>
              <tr>
                <td>Google Calendar</td>
                <td>Booking calendar events</td>
                <td>[BUSINESS TO CONFIRM]</td>
              </tr>
              <tr>
                <td>Zoho</td>
                <td>CRM — managing your enquiry and review (a Deal is created when you book)</td>
                <td>[BUSINESS TO CONFIRM — possible international transfer]</td>
              </tr>
              <tr>
                <td>Google Tag Manager / Google Analytics 4</td>
                <td>Usage analytics (consent-gated)</td>
                <td>[BUSINESS TO CONFIRM]</td>
              </tr>
              <tr>
                <td>Trustpilot</td>
                <td>Trust score widget (consent-gated)</td>
                <td>[BUSINESS TO CONFIRM — possible international transfer]</td>
              </tr>
            </tbody>
          </table>
          <p className="itk-legal-flag">
            [BUSINESS TO CONFIRM: DPAs (data processing agreements) with each processor — a commercial task,
            not code. Confirm international transfer safeguards for Zoho and Trustpilot.]
          </p>
        </section>

        <section>
          <h2>5. How long we keep your data</h2>
          <table className="itk-legal-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Retention</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Account profile</td><td>Account lifetime — deleted when you delete your account</td></tr>
              <tr><td>Ideas, learning progress, uploaded documents</td><td>Account lifetime — deleted when you delete your account</td></tr>
              <tr><td>Confidentiality agreements</td><td>Account lifetime — then retained anonymously for 15 years (NDA clause 8)</td></tr>
              <tr><td>Bookings</td><td>Account lifetime — your user id is anonymised when you delete your account; business records retained</td></tr>
              <tr><td>Analytics events</td><td>26 months, then automatically deleted</td></tr>
              <tr><td>Sign-in codes (OTP)</td><td>10 minutes, then automatically deleted</td></tr>
              <tr><td>Web push tokens</td><td>Account lifetime — deleted when you delete your account</td></tr>
              <tr><td>Server logs</td><td>30 days</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>6. Your rights</h2>
          <p>Under the UK GDPR you have the following rights:</p>
          <ul>
            <li><strong>Access</strong> — you can download a copy of all your data from the Settings page.</li>
            <li><strong>Rectification</strong> — you can correct your data in the app (ideas, settings, email address). NDAs cannot be amended once signed.</li>
            <li><strong>Erasure</strong> — you can delete your account from the Settings page.</li>
            <li><strong>Restriction</strong> — you can ask us to restrict processing of your data.</li>
            <li><strong>Portability</strong> — you can download your data in a machine-readable JSON format from the Settings page.</li>
            <li><strong>Objection</strong> — you can object to marketing at any time via the Alert Centre toggle.</li>
            <li><strong>Automated decision-making</strong> — we do not make solely automated decisions with legal or significant effects about you. Coach matching is automated but does not produce legal effects.</li>
            <li><strong>Withdrawal of consent</strong> — you can withdraw consent for analytics cookies and marketing at any time (via the cookie settings link in the footer or the Alert Centre).</li>
          </ul>
          <p>
            To exercise any of these rights, contact us at{' '}
            <span className="itk-legal-flag">[BUSINESS TO CONFIRM — DSAR contact email/address]</span>.
            We respond within one month (the statutory period).
          </p>
          <p>
            If you are unhappy with how we handle your data, you have the right to complain to the
            Information Commissioner&rsquo;s Office (ICO) at ico.org.uk or on 0303 123 1113.
          </p>
        </section>

        <section>
          <h2>7. International transfers</h2>
          <p>
            Most of your data is processed in the UK and EU (Google Cloud europe-west2, Resend eu-west-1).
            Some processors (Zoho, Trustpilot) may transfer data outside the UK/EEA.
          </p>
          <p className="itk-legal-flag">
            [BUSINESS TO CONFIRM: the specific transfer mechanism for each processor (UK adequacy regulations,
            Standard Contractual Clauses, or other safeguards).]
          </p>
        </section>

        <section>
          <h2>8. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. The &ldquo;last updated&rdquo; date at the top of the
            page will reflect the most recent revision.
          </p>
        </section>
      </div>
    </div>
  );
}